import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useDarkMode } from "../../utils/useDarkMode";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface BookingTrendChartProps {
  data: Array<{ date: string; bookings: number }>;
  height?: number;
  className?: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

// ── Custom tooltip ────────────────────────────────────────────────────────────

function BookingTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? 0;

  return (
    <div className="rounded-lg border border-border-default bg-surface shadow-dropdown px-3 py-2">
      <p className="text-xs text-content-tertiary mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-content-primary">
        {value} {value === 1 ? "booking" : "bookings"}
      </p>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function BookingTrendChart({ data, height = 260, className }: BookingTrendChartProps) {
  const isDark = useDarkMode();
  const brandColor = isDark ? "#7C3AED" : "#5B21B6";

  return (
    <div className={className} style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 0 }} barCategoryGap="30%">
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-default)" vertical={false} />

          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: "var(--color-content-tertiary)" }}
            axisLine={false}
            tickLine={false}
            dy={6}
          />

          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: "var(--color-content-tertiary)" }}
            axisLine={false}
            tickLine={false}
            width={32}
          />

          <Tooltip content={<BookingTooltip />} cursor={{ fill: "var(--color-border-default)", opacity: 0.5 }} />

          <Bar
            dataKey="bookings"
            fill={brandColor}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

BookingTrendChart.displayName = "BookingTrendChart";
